import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useNotification } from 'hooks'
import { useAssignmentQuery, usePartnerQuery } from '../../../api'
import { InfoItems } from 'components/core-ui/InfoItems'
import { Box, CircularProgress, Grid } from '@mui/material'
import style from '../../../partners.modules.scss'
import ActionComponents from '../../../components/ActionComponents'
import { formatUIDate, formatUIDateTime } from 'utils/helpers'
import { Link } from 'components/core-ui/Link'
import { EditIdentityDetailsModal } from './EditIdentityDetailsModal'
import { EditAssignmentDetailsModal } from './EditAssignmentDetailsModal'
import { EditManagementDetailsModal } from './EditManagementDetailsModal'
import dayjs from 'dayjs'
import { getActiveAssignment } from '../../helpers'

const AssignmentDetails: React.FC = () => {
  const [isEditIdentityDetailsOpen, setIsEditIdentityDetailsOpen] = useState<boolean>(false)
  const [isEditAssignmentDetailsOpen, setIsEditAssignmentDetailsOpen] = useState<boolean>(false)
  const [isEditAssignmentManagementOpen, setIsEditAssignmentManagementOpen] =
    useState<boolean>(false)

  const { id } = useParams<{ id: string }>()

  const { showNotification } = useNotification()

  const {
    data: assignment,
    isLoading,
    refetch
  } = useAssignmentQuery(showNotification, Number(id), false)

  const openEditIdentityDetailsModal = () => setIsEditIdentityDetailsOpen(true)
  const closeEditIdentityDetailsModal = () => setIsEditIdentityDetailsOpen(false)

  const openEditAssignmentDetailsModal = () => setIsEditAssignmentDetailsOpen(true)
  const closeEditAssignmentDetailsModal = () => setIsEditAssignmentDetailsOpen(false)

  const openEditAssignmentManagementModal = () => setIsEditAssignmentManagementOpen(true)
  const closeEditAssignmentManagementModal = () => setIsEditAssignmentManagementOpen(false)

  const { data: partner } = usePartnerQuery(showNotification, Number(assignment?.person?.id), false)

  const activeAssignment = useMemo(() => {
    if (!partner) return null
    const assignment = getActiveAssignment(partner.assignmentList, true)
    return assignment?.id === Number(id) ? null : assignment
  }, [partner?.assignmentList])

  if (isLoading)
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  if (!assignment) return <div>Assignment not found</div>

  return (
    <div className={style.mainContainer}>
      <div className={style.breadcrumbsContainer}>
        <Link href="/iam/partners" className={style.breadcrumbsLink}>
          External Partners Management
        </Link>
        <span className={style.breadcrumbsSeparator}>|</span>
        <Link href="/iam/externalPartners/assignments" className={style.breadcrumbsLink}>
          Assignments
        </Link>
        <span className={style.breadcrumbsSeparator}>|</span>
        <span className={style.breadcrumbsCurrent}>
          {assignment.person?.firstName} {assignment.person?.lastName} - {assignment.globalId}
        </span>
      </div>
      <Box className={style.flexContainer}>
        <Box className={style.infoItemsContainer}>
          <EditIdentityDetailsModal
            isModalOpen={isEditIdentityDetailsOpen}
            onModalClose={closeEditIdentityDetailsModal}
            person={assignment.person}
            assignmentId={assignment.id}
            refetchAssignment={refetch}
          />
          <EditAssignmentDetailsModal
            isModalOpen={isEditAssignmentDetailsOpen}
            onModalClose={closeEditAssignmentDetailsModal}
            assignment={assignment}
            refetchAssignment={refetch}
          />
          <InfoItems
            itemsPerRow={2}
            itemsSpacing={2}
            items={[
              {
                name: assignment.person?.firstName || '',
                label: 'Name:',
                render: () => (
                  <Link
                    href={`/iam/externalPartners/partner/${assignment.person?.id}`}
                    style={{ textDecoration: 'none' }}
                  >
                    {assignment.person?.firstName} {assignment.person?.lastName}
                  </Link>
                )
              },
              { name: assignment.person?.status || '', label: 'Status:' },
              { name: assignment.person?.email || 'Not provided', label: 'Email:' },
              { name: assignment.externalPartnerType?.name || '', label: 'Account type:' },
              { name: assignment.person?.phone || 'Not provided', label: 'Phone number:' }
            ]}
            title="Identity details"
            actionComponents={
              !activeAssignment && dayjs(assignment.endDate).add(1, 'month') >= dayjs() ? (
                <ActionComponents openEditModal={openEditIdentityDetailsModal} />
              ) : null
            }
          />
          <InfoItems
            itemsPerRow={2}
            itemsSpacing={2}
            items={[
              { name: assignment.status?.name || '', label: 'Status:' },
              { name: assignment.googleAccount ? 'Yes' : 'No', label: 'Google access:' },
              { name: assignment?.externalPartnerType?.name || '', label: 'Type:' },
              { name: assignment.adAccount ? 'Yes' : 'No', label: 'Active directory:' },
              { name: assignment.jobTitle, label: 'Job Title:' },
              { name: assignment.ldapAccount ? 'Yes' : 'No', label: 'LDAP Account:' },
              {
                name: assignment.email && assignment.email !== 'null' ? assignment.email : '',
                label: 'Internal Email:'
              },
              { name: formatUIDate(assignment.startDate), label: 'Start Date:' },
              {
                name: `${assignment.legalEntity?.name}`,
                label: 'Legal entity:',
                render: () => (
                  <Link
                    href={`/iam/externalPartners/legalEntities/${assignment.legalEntity?.id}`}
                    style={{ textDecoration: 'none' }}
                  >
                    {assignment.legalEntity?.name}
                  </Link>
                )
              },
              { name: formatUIDate(assignment.endDate), label: 'End Date:' },
              { name: '', label: '' }
            ]}
            title="Assignment details"
            actionComponents={
              !activeAssignment && dayjs(assignment.endDate).add(1, 'month') >= dayjs() ? (
                <ActionComponents openEditModal={() => openEditAssignmentDetailsModal()} />
              ) : null
            }
          />
        </Box>
        <Box className={style.infoItemsContainer}>
          <EditManagementDetailsModal
            isModalOpen={isEditAssignmentManagementOpen}
            onModalClose={closeEditAssignmentManagementModal}
            assignment={assignment}
            refetchAssignment={refetch}
          />
          <InfoItems
            itemsPerRow={1}
            itemsSpacing={2}
            items={[
              {
                name: `${assignment.manager?.firstName} ${assignment.manager?.lastName}`,
                label: 'Manager:',
                render: () => (
                  <Link
                    href={`/iam/externalPartners/managers/${assignment.manager?.id}`}
                    style={{ textDecoration: 'none' }}
                  >
                    {assignment.manager?.firstName + ' ' + assignment.manager?.lastName}
                  </Link>
                )
              },
              { name: assignment.manager?.email || '', label: 'Manager email:' },
              ...(assignment.delegate
                ? [
                    {
                      name: `${assignment.delegate.firstName} ${assignment.delegate.lastName}`,
                      label: 'Delegate:',
                      render: () => (
                        <Link
                          href={`/iam/externalPartners/delegates/${assignment.delegate?.id}`}
                          style={{ textDecoration: 'none' }}
                        >
                          {assignment.delegate?.firstName + ' ' + assignment.delegate?.lastName}
                        </Link>
                      )
                    },
                    { name: `${assignment.delegate.email}`, label: 'Delegate email:' }
                  ]
                : [])
            ]}
            title="Assignment management"
            actionComponents={
              !activeAssignment && dayjs(assignment.endDate).add(1, 'month') >= dayjs() ? (
                <ActionComponents openEditModal={() => openEditAssignmentManagementModal()} />
              ) : null
            }
          />
          <InfoItems
            itemsPerRow={2}
            itemsSpacing={2}
            items={[
              { name: assignment.createdBy || '', label: 'Created by:' },
              { name: formatUIDateTime(assignment.createdDate), label: 'Created Date:' },
              { name: assignment.lastModifiedBy || '', label: 'Modified by:' },
              { name: formatUIDateTime(assignment.lastModifiedDate), label: 'Modified date:' },
              { name: assignment.person?.globalId || '', label: 'Partner ID:' },
              { name: assignment.globalId || '', label: 'Marca:' },
              { name: `${assignment.extensionPeriod}`, label: 'Extension:' },
              ...(assignment.userName
                ? [{ name: `${assignment.userName}`, label: 'User name:' }]
                : [])
            ]}
            title={'Assignment metadata'}
          />
        </Box>
      </Box>
      <Grid container spacing={2} style={{ marginTop: '4px' }}>
        {assignment.stores.map((store: any) => (
          <Grid item xs={12} md={6} key={store.id}>
            <InfoItems
              itemsPerRow={2}
              itemsSpacing={2}
              items={[
                {
                  name: `${store.name}`,
                  label: 'Name:',
                  render: () => (
                    <Link
                      href={`/iam/externalPartners/stores/${store.id}`}
                      style={{ textDecoration: 'none' }}
                    >
                      {store.name}
                    </Link>
                  )
                },
                {
                  name: `${
                    store.externalIdList
                      ? store.externalIdList.find(
                          (externalId: any) => externalId.system.name === 'SAP'
                        )?.value || ''
                      : ''
                  }`,
                  label: 'SAP code:'
                },
                { name: `${store.type}`, label: 'Store type:' },
                { name: `${store.isEnabled ? 'Enabled' : 'Disabled'}`, label: 'Status:' },
                {
                  name: `${assignment.legalEntity?.name}`,
                  label: 'Managed by:',
                  render: () => (
                    <Link
                      href={`/iam/externalPartners/legalEntities/${assignment.legalEntity?.id}`}
                      style={{ textDecoration: 'none' }}
                    >
                      {assignment.legalEntity?.name}
                    </Link>
                  )
                },
                {
                  name: `${formatUIDate(store.createdDate)}`,
                  label: 'Start date:'
                }
              ]}
              title="Assigned store"
            />
          </Grid>
        ))}
      </Grid>
      {(assignment.person?.details || assignment.details || assignment.updateComment) && (
        <Grid container spacing={2} style={{ marginTop: '4px' }}>
          <Grid item xs={12}>
            <InfoItems
              itemsPerRow={1}
              itemsSpacing={2}
              items={[
                ...(assignment.person?.details
                  ? [
                      {
                        name: assignment.person.details,
                        label: 'Partner details:',
                        labelSize: 2,
                        valueSize: 10
                      }
                    ]
                  : []),
                ...(assignment.details
                  ? [
                      {
                        name: assignment.details,
                        label: 'Assignment details:',
                        labelSize: 2,
                        valueSize: 10
                      }
                    ]
                  : []),
                ...(assignment.updateComment
                  ? [
                      {
                        name: assignment.updateComment,
                        label: 'Comments:',
                        labelSize: 2,
                        valueSize: 10
                      }
                    ]
                  : [])
              ]}
              title={'Assignment extra information'}
            />
          </Grid>
        </Grid>
      )}
    </div>
  )
}

export default AssignmentDetails
